import { useCallback, useRef, useState } from 'react';

/**
 * Хук для защиты от дублирования отправки форм
 *
 * Предотвращает множественную отправку формы при быстрых повторных кликах.
 *
 * @example
 * ```tsx
 * const { isSubmitting, protectedSubmit } = useFormSubmitProtection();
 *
 * const handleSubmit = protectedSubmit(async (data) => {
 *   await sendFormData(data);
 *   // успешная отправка
 * });
 *
 * <Button disabled={isSubmitting} onClick={handleSubmit}>
 *   {isSubmitting ? 'Отправка...' : 'Отправить'}
 * </Button>
 * ```
 */
export const useFormSubmitProtection = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const submissionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Обертка для функции отправки с защитой от дублирования
   *
   * @param submitFn - Асинхронная функция отправки формы
   * @param options - Опции защиты
   */
  const protectedSubmit = useCallback(
    <T extends unknown[]>(
      submitFn: (...args: T) => Promise<void>,
      options?: { timeout?: number },
    ) => {
      return async (...args: T) => {
        // Проверяем, не выполняется ли уже отправка
        if (isSubmittingRef.current) {
          console.warn(
            '[FormSubmitProtection] Повторная отправка заблокирована',
          );
          return;
        }

        isSubmittingRef.current = true;

        setIsSubmitting(true);

        // Устанавливаем таймаут на случай зависания
        const timeoutMs = options?.timeout || 30000; // 30 секунд по умолчанию

        submissionTimeoutRef.current = setTimeout(() => {
          console.error('[FormSubmitProtection] Превышен таймаут отправки');

          isSubmittingRef.current = false;

          setIsSubmitting(false);
        }, timeoutMs);

        try {
          await submitFn(...args);
        } catch (error) {
          console.error('[FormSubmitProtection] Ошибка при отправке:', error);
          throw error;
        } finally {
          // Очищаем таймаут
          if (submissionTimeoutRef.current) {
            clearTimeout(submissionTimeoutRef.current);
            submissionTimeoutRef.current = null;
          }

          setIsSubmitting(false);

          isSubmittingRef.current = false;
        }
      };
    },
    [],
  );

  /**
   * Ручной сброс состояния (для edge cases)
   */
  const reset = useCallback(() => {
    if (submissionTimeoutRef.current) {
      clearTimeout(submissionTimeoutRef.current);
      submissionTimeoutRef.current = null;
    }
    setIsSubmitting(false);
    isSubmittingRef.current = false;
  }, []);

  return {
    /** Флаг состояния отправки */
    isSubmitting,

    /** Функция-обертка для защиты submit handler от дублирования */
    protectedSubmit,

    /** Ручной сброс состояния отправки */
    reset,
  };
};
